import { Box, Flex, Grid, Heading, Text } from "@chakra-ui/react"
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import type { IconType } from "react-icons"
import {
  FiActivity,
  FiCpu,
  FiFolder,
  FiHash,
  FiLayers,
  FiTag,
} from "react-icons/fi"

import { IconBox, VisionCard } from "@/components/VisionUI"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-tags")({
  component: AdminTags,
})

type AdminTagsPath =
  | "/tags"
  | "/tag-types"
  | "/tag-states"
  | "/tag-templates"
  | "/tag-categories"
  | "/tag-sku-editor"
  | "/tag-skus"

interface AdminTagsTile {
  titleKey: string
  descKey: string
  defaultTitle: string
  defaultDesc: string
  icon: IconType
  to: AdminTagsPath
}

function AdminTags() {
  const sc = useSemanticColors()
  const { t } = useTranslation("admin")
  const { pathname } = useLocation()
  const isOverview = pathname === "/admin-tags" || pathname === "/admin-tags/"

  const tiles: AdminTagsTile[] = [
    {
      titleKey: "tagsMenu.tiles.browser",
      defaultTitle: "Tag Browser",
      descKey: "tagsMenu.tiles.browserDesc",
      defaultDesc: "Browse and edit every tag.",
      icon: FiCpu,
      to: "/tags",
    },
    {
      titleKey: "tagsMenu.tiles.types",
      defaultTitle: "Tag Types",
      descKey: "tagsMenu.tiles.typesDesc",
      defaultDesc:
        "The fixed catalog of tag types (backend enum) and the templates, categories, and SKUs bound to each. Read-only.",
      icon: FiLayers,
      to: "/tag-types",
    },
    {
      titleKey: "tagsMenu.tiles.states",
      defaultTitle: "Tag States",
      descKey: "tagsMenu.tiles.statesDesc",
      defaultDesc:
        "The fixed lifecycle states (backend enum) and the types, templates, categories, and SKUs of tags currently in each. Read-only.",
      icon: FiActivity,
      to: "/tag-states",
    },
    {
      titleKey: "tagsMenu.tiles.templates",
      defaultTitle: "Tag Templates",
      descKey: "tagsMenu.tiles.templatesDesc",
      defaultDesc:
        "Operational templates that drive ingestion and per-axis matching.",
      icon: FiTag,
      to: "/tag-templates",
    },
    {
      titleKey: "tagsMenu.tiles.categories",
      defaultTitle: "Tag Categories",
      descKey: "tagsMenu.tiles.categoriesDesc",
      defaultDesc:
        "Catalog of categories — defaults are protected; deletes cascade to default.",
      icon: FiFolder,
      to: "/tag-categories",
    },
    {
      titleKey: "tagsMenu.tiles.skuEditor",
      defaultTitle: "Tag SKU Editor",
      descKey: "tagsMenu.tiles.skuEditorDesc",
      defaultDesc:
        "Catalog of GS1 SKUs — defaults are protected; deletes cascade to default. Drives the DPP record join.",
      icon: FiHash,
      to: "/tag-sku-editor",
    },
    {
      titleKey: "tagsMenu.tiles.skuBindings",
      defaultTitle: "Tag SKUs bindings",
      descKey: "tagsMenu.tiles.skuBindingsDesc",
      defaultDesc: "Set each tag's GS1 SKU — picks from the SKU catalog above.",
      icon: FiHash,
      to: "/tag-skus",
    },
  ]

  return (
    <>
      {isOverview && (
        <Box>
          <Box mb={8}>
            <Heading size="xl" color={sc.heading} mb={2}>
              {t("tagsMenu.title", "Tags")}
            </Heading>
            <Text color={sc.textMuted}>
              {t(
                "tagsMenu.subtitle",
                "Manage tags, operational templates, categories, and SKUs.",
              )}
            </Text>
          </Box>

          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xl: "repeat(4, 1fr)",
            }}
            gap={4}
          >
            {tiles.map((tile) => (
              <Link
                key={tile.to}
                to={tile.to}
                style={{ textDecoration: "none" }}
              >
                <VisionCard
                  variant="elevated"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "xl",
                  }}
                >
                  <Flex
                    direction="column"
                    align="center"
                    textAlign="center"
                    py={5}
                    px={4}
                  >
                    <IconBox variant="glass" size="lg" mb={3}>
                      <tile.icon fontSize="24px" color={sc.iconColor} />
                    </IconBox>
                    <Heading size="sm" color={sc.heading} mb={1}>
                      {t(tile.titleKey, tile.defaultTitle)}
                    </Heading>
                    <Text color={sc.textMuted} fontSize="xs">
                      {t(tile.descKey, tile.defaultDesc)}
                    </Text>
                  </Flex>
                </VisionCard>
              </Link>
            ))}
          </Grid>
        </Box>
      )}
      <Outlet />
    </>
  )
}
